import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AT_STATES, LINE_STATES, eur, tempoDesde, numRef } from "@/lib/constants";
import { ArrowLeft, History, FileText } from "lucide-react";

const TIPOS = {
  cliente: { endpoint: "clientes", titulo: "Cliente" },
  veiculo: { endpoint: "veiculos", titulo: "Veículo" },
  artigo: { endpoint: "artigos", titulo: "Artigo" },
};

export default function EntidadeHistorico() {
  const { tipo, id } = useParams();
  const navigate = useNavigate();
  const [ent, setEnt] = useState(null);
  const [eventos, setEventos] = useState([]);
  const cfg = TIPOS[tipo];

  useEffect(() => {
    if (!cfg) return;
    api.get(`/${cfg.endpoint}/${id}`).then((r) => setEnt(r.data));
    api.get("/eventos", { params: { entity_id: id } }).then((r) => setEventos(r.data));
  }, [tipo, id, cfg]);

  if (!cfg) return <div className="p-8 text-slate-400">Tipo inválido.</div>;
  if (!ent) return <div className="p-8 text-slate-400 text-sm">A carregar…</div>;

  const ats = ent.atendimentos || [];
  const titulo = tipo === "cliente" ? ent.nome
    : tipo === "veiculo" ? ent.matricula
    : ent.descricao;

  return (
    <div className="max-w-5xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3" data-testid="btn-voltar">
        <ArrowLeft size={15} /> Voltar
      </button>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 mb-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700">{cfg.titulo}</div>
        <div className="flex items-center gap-3 mt-1">
          {tipo === "veiculo" ? <span className="plate text-base">{ent.matricula}</span> : <h1 className="text-xl font-bold text-slate-900">{titulo}</h1>}
          {tipo === "veiculo" && <span className="text-slate-600">{ent.marca} {ent.modelo} {ent.motorizacao}</span>}
          {tipo === "cliente" && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{ent.tipo}</span>}
          {tipo === "artigo" && <span className="font-mono text-sm text-slate-500">{ent.ref_marca} {ent.marca ? `· ${ent.marca}` : ""} {ent.ref_toconline ? `· TOC ${ent.ref_toconline}` : ""}</span>}
        </div>
        {tipo === "cliente" && <div className="text-sm text-slate-500 mt-1">{ent.nif ? `NIF ${ent.nif} · ` : ""}{ent.telefone || ""}</div>}
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-8">
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Pedidos ({ats.length})</h3>
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm divide-y divide-slate-100">
            {ats.map((a) => {
              const total = (a.linhas || []).reduce((s, l) => s + (l.total || 0), 0);
              return (
                <button key={a.id} onClick={() => navigate(`/pedido/${a.id}`)} data-testid={`hist-pedido-${a.numero}`}
                  className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50 relative">
                  <span className={`absolute left-0 top-0 bottom-0 w-1 ${AT_STATES[a.estado]?.strip || "bg-slate-300"}`} />
                  <FileText size={15} className="text-sky-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800">{numRef(a.numero, a.codigo)} · {a.cliente?.nome || "—"} {a.veiculo?.matricula ? `· ${a.veiculo.matricula}` : ""}</div>
                    <div className="text-[11px] text-slate-500 truncate">{a.descricao_inicial || `${(a.linhas || []).length} linha(s)`}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs font-bold text-slate-800">{eur(total)}</div>
                    <div className="text-[10px] text-slate-400">{AT_STATES[a.estado]?.label || a.estado} · {tempoDesde(a.created_at)}</div>
                  </div>
                </button>
              );
            })}
            {!ats.length && <div className="text-sm text-slate-400 text-center py-8">Sem pedidos associados.</div>}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4">
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><History size={14} /> Histórico</h3>
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 space-y-2.5 max-h-[60vh] overflow-y-auto">
            {eventos.map((e) => (
              <div key={e.id} className="text-xs border-l-2 border-slate-200 pl-2.5">
                <div className="text-slate-700">{e.descricao}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{e.user_name} · {tempoDesde(e.created_at)}</div>
              </div>
            ))}
            {!eventos.length && <div className="text-xs text-slate-400">Sem eventos.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
